import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiShoppingCart } from "react-icons/fi";
import { API_BASE_URL } from "../../../config";

// Endpoint de la API para obtener los productos
const API_ORDENES = `${API_BASE_URL}/orders/clover_products_online`;

const OnlineOrdering = () => {
  const { cliente_id: paramClienteId } = useParams();
  const navigate = useNavigate();
  const [cliente_id, setClienteId] = useState(paramClienteId || localStorage.getItem("cliente_id") || "");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem("cart")) || []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null); // Producto seleccionado para el modal
  const [selectedModifiers, setSelectedModifiers] = useState({}); // Modificadores seleccionados

  useEffect(() => {
    if (!cliente_id) {
      navigate("/seleccionar-cliente");
      return;
    }

    localStorage.setItem("cliente_id", cliente_id);

    fetch(`${API_ORDENES}?cliente_id=${cliente_id}`)
      .then(response => response.ok ? response.json() : Promise.reject("Error al obtener datos"))
      .then(data => {
        setCategories(data.categorias || []);
        setSelectedCategory(data.categorias.length > 0 ? data.categorias[0].id : null);
        setLoading(false);
      })
      .catch(error => {
        console.error("Error:", error);
        setError("Error al cargar categorías.");
        setCategories([]);
        setLoading(false);
      });
  }, [cliente_id]);

  // Manejar selección de modificadores
  const handleModifierChange = (groupId, modifier) => {
    setSelectedModifiers((prevModifiers) => {
      const currentSelection = prevModifiers[groupId] || [];

      // Si el modificador ya está seleccionado, quitarlo
      if (currentSelection.some(m => m.id === modifier.id)) {
        return { ...prevModifiers, [groupId]: currentSelection.filter(m => m.id !== modifier.id) };
      }

      return { ...prevModifiers, [groupId]: [...currentSelection, modifier] };
    });
  };

  // Calcular el precio total del producto con modificadores
  const calculateTotalPrice = () => {
    if (!selectedProduct) return 0;

    const basePrice = selectedProduct.price;
    const modifiersTotal = Object.values(selectedModifiers)
      .flat()
      .reduce((total, mod) => total + mod.price, 0);

    return (basePrice + modifiersTotal).toFixed(2);
  };

const addToCart = () => {
  if (!selectedProduct) return;

  const productWithModifiers = {
    ...selectedProduct,
    selectedModifiers: selectedModifiers,
    price: selectedProduct.price || 0,
    totalPrice: parseFloat(calculateTotalPrice()),
    taxes: selectedProduct.taxes || []  // Incluir los impuestos del producto
  };

  const newCart = [...cart, productWithModifiers];
  setCart(newCart);
  localStorage.setItem("cart", JSON.stringify(newCart));

  setSelectedProduct(null);
  setSelectedModifiers({});
};

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold">Ordena en Línea</h2>
        <button onClick={() => navigate(`/carrito/${cliente_id}`)} className="relative flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg">
          <FiShoppingCart className="mr-2" />
          Carrito <span className="ml-2 bg-red-500 text-white px-2 py-1 rounded-full text-sm">{cart.length}</span>
        </button>
      </div>

      {loading ? (
        <p className="text-center text-gray-500">Cargando categorías...</p>
      ) : error ? (
        <p className="text-red-500 text-center">{error}</p>
      ) : (
        <>
          {/* Mostrar Categorías */}
          <div className="flex overflow-x-auto space-x-2 pb-3">
            {categories.map(category => (
              <button
                key={category.id}
                className={`px-4 py-2 rounded-lg text-white ${
                  selectedCategory === category.id ? "bg-green-500" : "bg-gray-500"
                }`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Mostrar Productos de la Categoría Seleccionada */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-6">
            {categories.find(cat => cat.id === selectedCategory)?.productos.map(product => (
              <div key={product.id} className="bg-white rounded-lg shadow-lg p-4 transform transition hover:scale-105">
                <img src="https://via.placeholder.com/150" alt={product.name} className="w-full h-40 object-cover rounded-md" />
                <h4 className="text-lg font-semibold mt-2">{product.name}</h4>
                <p className="text-gray-600">${product.price.toFixed(2)}</p>
                <button onClick={() => setSelectedProduct(product)} className="w-full bg-green-500 text-white py-2 mt-3 rounded-md hover:bg-green-600">
                  Personalizar
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Modal para Selección de Modificadores */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-6 rounded-lg w-96">
            <h3 className="text-xl font-bold mb-4">{selectedProduct.name}</h3>
            <p className="mb-4">Precio Base: ${selectedProduct.price.toFixed(2)}</p>

            {selectedProduct.modifiers?.map(modifierGroup => (
              <div key={modifierGroup.group_id} className="mb-3">
                <h4 className="font-semibold">{modifierGroup.name}</h4>
                {modifierGroup.modifiers.map(modifier => (
                  <label key={modifier.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      onChange={() => handleModifierChange(modifierGroup.group_id, modifier)}
                    />
                    <span>{modifier.name} (+${modifier.price.toFixed(2)})</span>
                  </label>
                ))}
              </div>
            ))}

            <p className="mt-4 font-bold">Total: ${calculateTotalPrice()}</p>

            <button onClick={addToCart} className="bg-blue-500 text-white px-4 py-2 mt-3 rounded-md">Agregar al Carrito</button>
            <button onClick={() => setSelectedProduct(null)} className="bg-gray-500 text-white px-4 py-2 mt-3 rounded-md ml-2">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnlineOrdering;
